import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit, Trash2, Search, Filter, Camera, User, Upload, Download } from 'lucide-react';
import Layout from '../components/Layout';
import { useApp } from '../contexts/AppContext';
import { Student } from '../types';
import toast from 'react-hot-toast';

const StudentsPage: React.FC = () => {
  const { students, classes, addStudent, updateStudent, deleteStudent, loading } = useApp();
  const [showModal, setShowModal] = useState(false);
  const [editingStudent, setEditingStudent] = useState<Student | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClass, setSelectedClass] = useState<string>('all');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    parentName: '',
    parentEmail: '',
    notes: ''
  });

  const filteredStudents = students.filter(student => {
    const matchesSearch = `${student.firstName} ${student.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         student.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         student.parentName?.toLowerCase().includes(searchTerm.toLowerCase());
    // For now, show all students regardless of class filter since we need to implement class-student relationships
    return matchesSearch;
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      toast.error('Please fill in required fields (First name and Last name)');
      return;
    }

    try {
      if (editingStudent) {
        await updateStudent(editingStudent.id, formData);
        toast.success('Student updated successfully');
      } else {
        const result = await addStudent(formData);
        
        // Show login credentials if student was created with email
        if (formData.email && result.loginCredentials) {
          toast.success(
            `Student added successfully! Login credentials: ${formData.email} / ${result.loginCredentials.password}`,
            { duration: 10000 }
          );
        } else if (formData.email) {
          toast.success(
            `Student added successfully! Login credentials: ${formData.email} / [Generated Password]`,
            { duration: 8000 }
          );
        } else {
          toast.success('Student added successfully');
        }
      }
      
      resetForm();
    } catch (error) {
      toast.error('Failed to save student');
    }
  };

  const handleEdit = (student: Student) => {
    setEditingStudent(student);
    setFormData({
      firstName: student.firstName,
      lastName: student.lastName,
      email: student.email || '',
      phone: student.phone || '',
      dateOfBirth: student.dateOfBirth || '',
      parentName: student.parentName || '',
      parentEmail: student.parentEmail || '',
      notes: student.notes || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    console.log('Deleting student with ID:', id);
    if (!id) {
      toast.error('Student ID is missing');
      return;
    }
    if (window.confirm('Are you sure you want to delete this student? This action cannot be undone.')) {
      try {
        await deleteStudent(id);
        toast.success('Student deleted successfully');
      } catch (error) {
        toast.error('Failed to delete student');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      parentName: '',
      parentEmail: '',
      notes: ''
    });
    setEditingStudent(null);
    setShowModal(false);
  };

  const downloadCSVTemplate = () => {
    const headers = ['firstName', 'lastName', 'email', 'phone', 'dateOfBirth', 'parentName', 'parentEmail', 'notes'];
    const csvContent = headers.join(',') + '\n' + 
      'John,Doe,john@example.com,123-456-7890,2010-01-15,Jane Doe,jane@example.com,Good student\n' +
      'Jane,Smith,jane@example.com,234-567-8901,2011-03-22,John Smith,john@example.com,Excellent student';
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'students-template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const parseCSV = (csvText: string): any[] => {
    const lines = csvText.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      throw new Error('CSV file must have at least a header row and one data row');
    }

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const requiredHeaders = ['firstname', 'lastname'];
    const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
    
    if (missingHeaders.length > 0) {
      throw new Error(`Missing required columns: ${missingHeaders.join(', ')}`);
    }

    const students = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      if (values.length !== headers.length) {
        throw new Error(`Row ${i + 1} has incorrect number of columns`);
      }

      const student: any = {};
      headers.forEach((header, index) => {
        const value = values[index];
        if (value && value !== '') {
          // Map CSV headers to form data fields
          switch (header) {
            case 'firstname':
              student.firstName = value;
              break;
            case 'lastname':
              student.lastName = value;
              break;
            case 'email':
              student.email = value;
              break;
            case 'phone':
              student.phone = value;
              break;
            case 'dateofbirth':
              student.dateOfBirth = value;
              break;
            case 'parentname':
              student.parentName = value;
              break;
            case 'parentemail':
              student.parentEmail = value;
              break;
            case 'notes':
              student.notes = value;
              break;
          }
        }
      });

      // Validate required fields
      if (!student.firstName || !student.lastName) {
        throw new Error(`Row ${i + 1} is missing required fields (firstName and lastName)`);
      }

      students.push(student);
    }

    return students;
  };

  const handleCSVImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            const csvText = e.target?.result as string;
            const studentsToImport = parseCSV(csvText);
            
            if (studentsToImport.length === 0) {
              toast.error('No valid students found in CSV file');
              return;
            }

            toast.loading('Importing students...', { duration: 0 });

            // Import students one by one
            let successCount = 0;
            let errorCount = 0;
            const errors: string[] = [];
            
            for (const studentData of studentsToImport) {
              try {
                await addStudent(studentData);
                successCount++;
              } catch (error) {
                console.error('Failed to import student:', studentData, error);
                errorCount++;
                errors.push(`${studentData.name || 'Unknown'}: ${error instanceof Error ? error.message : 'Unknown error'}`);
              }
            }
            
            toast.dismiss();
            
            if (successCount > 0) {
              toast.success(`Successfully imported ${successCount} students`);
            }
            if (errorCount > 0) {
              console.error('Import errors:', errors);
              toast.error(`Failed to import ${errorCount} students. Check console for details.`);
            }
          } catch (error) {
            console.error('CSV import error:', error);
            toast.error(`CSV import failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  };

  const getClassById = (classId: string) => {
    return classes.find(c => c.id === classId);
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="space-y-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-20 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Students</h1>
            <p className="mt-2 text-gray-600 dark:text-gray-300">Manage your student profiles and information</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={downloadCSVTemplate}
              className="btn-secondary flex items-center"
              title="Download CSV template"
            >
              <Download className="h-5 w-5 mr-2" />
              Template
            </button>
            <button
              onClick={handleCSVImport}
              className="btn-secondary flex items-center"
              title="Import students from CSV"
            >
              <Upload className="h-5 w-5 mr-2" />
              Import CSV
            </button>
            <button
              onClick={() => setShowModal(true)}
              className="btn-primary flex items-center"
            >
              <Plus className="h-5 w-5 mr-2" />
              Add Student
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              placeholder="Search students..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 input-field"
            />
          </div>
          <div className="flex items-center space-x-2">
            <Filter className="h-5 w-5 text-gray-400 dark:text-gray-500" />
            <select
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              className="input-field"
            >
              <option value="all">All Classes</option>
              {classes.map(classItem => (
                <option key={classItem.id} value={classItem.id}>
                  {classItem.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {filteredStudents.length === 0 ? (
          <div className="text-center py-12">
            <User className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
              {searchTerm || selectedClass !== 'all' ? 'No students found' : 'No students'}
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {searchTerm || selectedClass !== 'all' 
                ? 'Try adjusting your search or filter criteria.'
                : 'Get started by adding a new student.'
              }
            </p>
            {!searchTerm && selectedClass === 'all' && (
              <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={downloadCSVTemplate}
                  className="btn-secondary flex items-center justify-center"
                  title="Download CSV template"
                >
                  <Download className="h-5 w-5 mr-2" />
                  Download Template
                </button>
                <button
                  onClick={handleCSVImport}
                  className="btn-secondary flex items-center justify-center"
                  title="Import students from CSV"
                >
                  <Upload className="h-5 w-5 mr-2" />
                  Import CSV
                </button>
                <button
                  onClick={() => setShowModal(true)}
                  className="btn-primary flex items-center justify-center"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Add Student
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredStudents.map((student) => (
              <div key={student.id} className="card hover:shadow-md transition-shadow">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    {student.photo ? (
                      <img
                        src={student.photo}
                        alt={`${student.firstName} ${student.lastName}`}
                        className="h-16 w-16 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-16 w-16 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center">
                        <User className="h-8 w-8 text-gray-400 dark:text-gray-500" />
                      </div>
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                          {student.firstName} {student.lastName}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {student.email || 'No email'}
                        </p>
                      </div>
                      <div className="flex space-x-1">
                        <Link
                          to={`/students/${student.id}`}
                          className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                          title="View Profile"
                        >
                          <User className="h-4 w-4" />
                        </Link>
                        <button
                          onClick={() => handleEdit(student)}
                          className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(student.id)}
                          className="text-gray-400 hover:text-red-600 dark:text-gray-500 dark:hover:text-red-400"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    
                    {student.email && (
                      <p className="text-sm text-gray-600 dark:text-gray-300 truncate">{student.email}</p>
                    )}
                    {student.parentName && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">Parent: {student.parentName}</p>
                    )}
                    
                    {student.notes && (
                      <p className="text-sm text-gray-600 dark:text-gray-300 mt-2 line-clamp-2">
                        {student.notes}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  {editingStudent ? 'Edit Student' : 'Add New Student'}
                </h3>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* First Name and Last Name - Full width row */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        First Name *
                      </label>
                      <input
                        type="text"
                        value={formData.firstName}
                        onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                        className="input-field"
                        placeholder="Enter first name"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        value={formData.lastName}
                        onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                        className="input-field"
                        placeholder="Enter last name"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        className="input-field"
                        placeholder="student@example.com"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        💡 If email is provided, student will automatically get login credentials with a random 8-character password
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Phone
                      </label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                        className="input-field"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date of Birth
                    </label>
                    <input
                      type="date"
                      value={formData.dateOfBirth}
                      onChange={(e) => setFormData(prev => ({ ...prev, dateOfBirth: e.target.value }))}
                      className="input-field"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Parent/Guardian Name
                      </label>
                      <input
                        type="text"
                        value={formData.parentName}
                        onChange={(e) => setFormData(prev => ({ ...prev, parentName: e.target.value }))}
                        className="input-field"
                        placeholder="Parent's full name"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Parent Email
                      </label>
                      <input
                        type="email"
                        value={formData.parentEmail}
                        onChange={(e) => setFormData(prev => ({ ...prev, parentEmail: e.target.value }))}
                        className="input-field"
                        placeholder="parent@example.com"
                      />
                    </div>
                  </div>
                  
                  
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Notes
                    </label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                      className="input-field"
                      rows={3}
                      placeholder="Additional notes about the student..."
                    />
                  </div>
                  
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={resetForm}
                      className="btn-secondary"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn-primary"
                    >
                      {editingStudent ? 'Update' : 'Add'} Student
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default StudentsPage;
